/**
 * useDocumentChat Hook
 * 
 * Manages document chat state for a specific job.
 * Receives complete answers (no streaming) via Pusher.
 * 
 * Similar to useTextbookChat but scoped to a specific job's documents.
 * This is a premium feature requiring the documentChat Cognito claim.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { askDocumentQuestion } from '../actions/document-chat/askDocumentQuestion';
import { getAuthHeader } from '../actions/utils/api';
import { getCurrentUserId } from '../utils/getCurrentUserId';
import { useRealtimeJob } from './useRealtimeJob';
import type { JobStatusUpdate } from './useAppSyncSubscription';

// Storage key prefix for persisting chat per job
const STORAGE_KEY_PREFIX = 'document-chat-';
const MAX_MESSAGES = 30; // Limit stored messages per job

// Serialized message type with timestamp as string
type SerializedChatMessage = Omit<DocumentChatMessage, 'timestamp'> & { timestamp: string };

interface StoredChatData {
  messages: SerializedChatMessage[];
  savedAt: string;
}

export interface DocumentChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  sources?: Array<{
    chunk_id: string;
    content: string;
    file_name?: string;
    similarity_score: number;
  }>;
  metadata?: {
    processing_time_ms?: number;
    chunks_searched?: number;
    chunks_used?: number;
    confidence?: number;
  };
  timestamp: Date;
  status?: 'sending' | 'queued' | 'processing' | 'completed' | 'error';
  errorMessage?: string;
}

interface UseDocumentChatOptions {
  sourceJobId: string;       // The job whose documents we're chatting about
  documentTitle?: string;    // Display name for the document/job
}

interface UseDocumentChatReturn {
  messages: DocumentChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (question: string) => Promise<void>;
  clearMessages: () => void;
  retryLastMessage: () => Promise<void>;
}

/**
 * Load chat data from localStorage for a specific job
 */
function loadFromStorage(sourceJobId: string): DocumentChatMessage[] | null {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${sourceJobId}`);
    if (!stored) return null;

    const data: StoredChatData = JSON.parse(stored);
    
    // Convert timestamp strings back to Date objects
    // Only restore completed messages (skip any in-progress ones)
    const messages: DocumentChatMessage[] = data.messages
      .filter(msg => msg.status === 'completed' || msg.status === 'error')
      .map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));

    return messages;
  } catch (err) {
    console.warn('[DocumentChat] Failed to load from localStorage:', err);
    return null;
  }
}

/**
 * Save chat data to localStorage for a specific job
 */
function saveToStorage(sourceJobId: string, messages: DocumentChatMessage[]): void {
  try {
    // Only save completed messages, limit to MAX_MESSAGES
    const messagesToSave = messages
      .filter(msg => msg.status === 'completed' || msg.status === 'error')
      .slice(-MAX_MESSAGES);

    const serializedMessages: StoredChatData['messages'] = messagesToSave.map(msg => {
      const { timestamp, ...rest } = msg;
      return {
        ...rest,
        timestamp: timestamp.toISOString()
      };
    });

    const data: StoredChatData = {
      messages: serializedMessages,
      savedAt: new Date().toISOString()
    };

    localStorage.setItem(`${STORAGE_KEY_PREFIX}${sourceJobId}`, JSON.stringify(data));
  } catch (err) {
    console.warn('[DocumentChat] Failed to save to localStorage:', err);
  }
}

/**
 * Clear chat data from localStorage for a specific job
 */
function clearStorage(sourceJobId: string): void {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${sourceJobId}`);
  } catch (err) {
    console.warn('[DocumentChat] Failed to clear localStorage:', err);
  }
}

export function useDocumentChat({ sourceJobId, documentTitle }: UseDocumentChatOptions): UseDocumentChatReturn {
  const [userId, setUserId] = useState<string>('');
  
  // Initialize state from localStorage
  const [messages, setMessages] = useState<DocumentChatMessage[]>(() => {
    return loadFromStorage(sourceJobId) || [];
  });
  
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track last user question for retry
  const lastQuestionRef = useRef<string | null>(null);
  
  // Get auth token for Pusher
  const getAuthToken = useCallback(async () => {
    const authHeader = await getAuthHeader();
    return authHeader.Authorization?.replace('Bearer ', '') || '';
  }, []);

  // Initialize userId from JWT on mount
  useEffect(() => {
    (async () => {
      const id = await getCurrentUserId();
      setUserId(id || '');
    })();
  }, []);

  // Reset messages when sourceJobId changes
  useEffect(() => {
    const storedMessages = loadFromStorage(sourceJobId);
    setMessages(storedMessages || []);
    setCurrentJobId(null);
    setError(null);
    setIsLoading(false);
  }, [sourceJobId]);

  // Persist messages to localStorage when they change
  useEffect(() => {
    const hasIncompleteMessages = messages.some(
      msg => msg.status === 'queued' || msg.status === 'processing'
    );
    
    if (!hasIncompleteMessages && messages.length > 0) {
      saveToStorage(sourceJobId, messages);
    }
  }, [messages, sourceJobId]);

  const markAssistantMessageError = useCallback((errorMessage: string) => {
    setMessages(prev => {
      const lastMsg = prev[prev.length - 1];
      if (lastMsg?.role === 'assistant') {
        return [
          ...prev.slice(0, -1),
          {
            ...lastMsg,
            status: 'error' as const,
            errorMessage,
            content: 'Sorry, something went wrong. Please try again.'
          }
        ];
      }
      return prev;
    });
  }, []);

  // Handle Pusher updates
  const handleChatUpdate = useCallback((update: JobStatusUpdate) => {
    console.log('[DocumentChat] Pusher update received:', update);

    // Check if this is a chat stream update
    if ((update as any).isChatStream) {
      const chatUpdate = update as any;
      const messageId = chatUpdate.chatMessageId;
      const content = chatUpdate.chatContent;
      const isComplete = chatUpdate.chatIsComplete;
      const sources = chatUpdate.chatSources;
      const metadata = chatUpdate.chatMetadata;
      const chatError = chatUpdate.chatError;

      // Handle error
      if (chatError) {
        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg?.role === 'assistant') {
            return [
              ...prev.slice(0, -1),
              {
                ...lastMsg,
                status: 'error',
                errorMessage: chatError,
                content: 'Sorry, I encountered an error processing your question.'
              }
            ];
          }
          return prev;
        });
        setIsLoading(false);
        return;
      }

      // Update or create assistant message
      setMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        
        // If last message is user message, add new assistant message
        if (lastMsg?.role === 'user') {
          return [
            ...prev,
            {
              id: messageId,
              role: 'assistant' as const,
              content,
              timestamp: new Date(),
              status: 'completed' as const,
              sources,
              metadata
            }
          ];
        }
        
        // Update existing assistant message if it exists
        if (lastMsg?.role === 'assistant' && lastMsg.id === messageId) {
          return [
            ...prev.slice(0, -1),
            {
              ...lastMsg,
              content,
              status: 'completed' as const,
              sources,
              metadata
            }
          ];
        }
        
        return prev;
      });

      // Mark as not loading when complete
      if (isComplete) {
        setIsLoading(false);
      }
    }
  }, []);

  useRealtimeJob({
    jobId: currentJobId,
    userId: userId || null,
    getAuthToken,
    enabled: Boolean(currentJobId && userId),
    onUpdate: handleChatUpdate,
    onCompleted: async () => {
      setIsLoading(false);
    },
    onFailed: async ({ error: jobError }) => {
      const errorMessage = jobError || 'Failed to process question';
      setError(errorMessage);
      setIsLoading(false);
      markAssistantMessageError(errorMessage);
    }
  });

  // Send message
  const sendMessage = useCallback(async (question: string) => {
    if (!question.trim() || isLoading || !userId) return;

    // Store for retry
    lastQuestionRef.current = question;

    // Add user message
    const userMessage: DocumentChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: question,
      timestamp: new Date(),
      status: 'completed'
    };
    setMessages(prev => [...prev, userMessage]);

    // Add placeholder assistant message
    const assistantMessage: DocumentChatMessage = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      status: 'queued'
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      setIsLoading(true);
      setError(null);

      // Call API
      const result = await askDocumentQuestion({
        question,
        sourceJobId
      });

      // Update state with job tracking info
      setCurrentJobId(result.jobId);

      // Update assistant message status
      setMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg?.role === 'assistant') {
          return [
            ...prev.slice(0, -1),
            { ...lastMsg, id: result.chatId, status: 'processing' as const }
          ];
        }
        return prev;
      });

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to send question';
      setError(errorMessage);
      setIsLoading(false);

      markAssistantMessageError(errorMessage);
    }
  }, [isLoading, markAssistantMessageError, userId, sourceJobId]);

  // Retry last message
  const retryLastMessage = useCallback(async () => {
    if (!lastQuestionRef.current) return;
    
    // Remove last two messages (user + failed assistant)
    setMessages(prev => prev.slice(0, -2));
    
    // Resend
    await sendMessage(lastQuestionRef.current);
  }, [sendMessage]);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
    setCurrentJobId(null);
    setError(null);
    setIsLoading(false);
    lastQuestionRef.current = null;
    clearStorage(sourceJobId);
  }, [sourceJobId]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    retryLastMessage
  };
}

